import { ButtonHTMLAttributes } from 'react'

import styled from '@emotion/styled'

import { mediaQueryUp } from '~/theme/utils/grid'
import { body, h6 } from '~/theme/utils/typography'

import Icon from '~/shared/atoms/Icon/IconView'
import { CarouselContent, CarouselItem } from '~/shared/molecules/Carousel'
import { useCarousel } from '~/shared/molecules/Carousel/CarouselApi'
import { BaseCarouselButton, CarouselButtonProps } from '~/shared/molecules/Carousel/CarouselUtils'

const ITEMS_GAP_MOBILE = '4px'
const ITEMS_GAP_DESKTOP = '6px'

const TAB_BORDER_BOTTOM_WIDTH = '3px'

export const ScrollableTabWrapper = styled.div`
  position: sticky;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.gray[20]};
  top: 0;
  min-width: 100%;
  padding-top: 24px;
  margin-left: -1rem;
  margin-right: -1.5rem;

  ${mediaQueryUp('m')} {
    padding-top: 38px;
  }
`

export const StyledCarouselContent = styled(CarouselContent)`
  padding-left: 16px;
  padding-right: 24px; // to account for scrollbar width
  margin-left: -${ITEMS_GAP_MOBILE};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[100]};

  & > div:first-of-type {
    margin-left: 0;
  }

  ${mediaQueryUp('m')} {
    margin-left: -${ITEMS_GAP_DESKTOP};
  }
`

export const StyledCarouselItem = styled(CarouselItem)`
  flex: 0 0 auto;
  padding-left: ${ITEMS_GAP_MOBILE};

  ${mediaQueryUp('m')} {
    padding-left: ${ITEMS_GAP_DESKTOP};
  }
`

export const ScrollableTab = styled.a<{ isActive: boolean }>`
  color: ${({ theme, isActive }) => (isActive ? theme.colors.gray[900] : theme.colors.gray[700])};
  border-bottom: ${props =>
    props.isActive ? `${TAB_BORDER_BOTTOM_WIDTH} solid ${props.theme.colors.orange[400]}` : `${TAB_BORDER_BOTTOM_WIDTH} solid transparent`};
  padding: 16px 8px;
  display: block;
  cursor: pointer;
  white-space: nowrap;
  ${props => body({ ...props, fontWeight: 'medium' })};

  ${mediaQueryUp('m')} {
    padding: 16px 12px;
    ${props => h6({ ...props, fontWeight: 'medium' })};
  }
`

export const StyledCarouselButton = styled(BaseCarouselButton)<
  CarouselButtonProps & ButtonHTMLAttributes<HTMLButtonElement> & { canScroll: boolean }
>`
  position: absolute;
  width: 54px;
  height: 100%;
  top: -${TAB_BORDER_BOTTOM_WIDTH};
  left: ${({ isNext }) => (isNext ? 'unset' : '0')};
  right: ${({ isNext }) => (isNext ? '0' : 'unset')};

  display: ${({ canScroll }) => (canScroll ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;

  background: ${({ isNext, theme }) =>
    isNext
      ? `linear-gradient(270deg, ${theme.colors.gray[20]} 80.36%, rgba(253, 253, 253, 0) 96.07%);`
      : `linear-gradient(90deg, ${theme.colors.gray[20]} 80.36%, rgba(253, 253, 253, 0) 96.07%);`};

  img {
    width: 10px;
    transform: ${({ isNext }) => (isNext ? undefined : 'rotate(180deg)')};
  }
`

export const CarouselButton = ({ isNext }: CarouselButtonProps) => {
  const { canScrollPrev, canScrollNext } = useCarousel()

  const canScroll = isNext ? canScrollNext : canScrollPrev

  return (
    <StyledCarouselButton isNext={isNext} canScroll={canScroll}>
      <Icon icon='chevron-right' />
    </StyledCarouselButton>
  )
}
